import React, { useCallback, useEffect, useRef, useState } from "react";
import { coreValues } from "./coreValues";

// A card is "active" once it reaches the middle band of the viewport. Using
// rootMargin for that band means no scroll listener is attached at all.
const CENTRE_BAND = { rootMargin: "-40% 0px -40% 0px", threshold: 0 };
const REVEAL_BAND = { rootMargin: "0px 0px -12% 0px", threshold: 0.15 };

// Returns the index of the card currently in the centre band (-1 for none) and
// the set of cards that have ever entered the viewport. Reveal latches on, so
// copy never fades back out once it has been read.
const useSpotlight = (count) => {
    const nodes = useRef([]);
    const inBand = useRef(new Set());
    const [active, setActive] = useState(-1);
    const [revealed, setRevealed] = useState(() => new Set());
    const [enhanced, setEnhanced] = useState(false);

    // (index, el) rather than a curried factory so each card can build one
    // stable callback ref instead of a fresh closure every render
    const setNode = useCallback((index, el) => {
        nodes.current[index] = el;
    }, []);

    useEffect(() => {
        // copied out of the ref so the cleanup below closes over this effect's
        // own Set rather than whatever the ref points at when it runs
        const band = inBand.current;
        const els = nodes.current.slice(0, count).filter(Boolean);
        // No observer support means no enhancement: every card stays in its
        // resting state, which is the fully visible one.
        if (!els.length || typeof IntersectionObserver === "undefined") return undefined;

        setEnhanced(true);

        const centre = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const i = Number(entry.target.dataset.index);
                if (entry.isIntersecting) band.add(i);
                else band.delete(i);
            });
            // Lowest index wins so the sequence always reads P -> M -> G, even
            // when two cards overlap the band on a short viewport.
            setActive(band.size ? Math.min(...band) : -1);
        }, CENTRE_BAND);

        const reveal = new IntersectionObserver((entries) => {
            const seen = entries.filter((e) => e.isIntersecting).map((e) => Number(e.target.dataset.index));
            if (!seen.length) return;
            seen.forEach((i) => {
                const el = nodes.current[i];
                if (el) reveal.unobserve(el);
            });
            setRevealed((prev) => {
                const next = new Set(prev);
                seen.forEach((i) => next.add(i));
                return next;
            });
        }, REVEAL_BAND);

        els.forEach((el) => {
            centre.observe(el);
            reveal.observe(el);
        });

        return () => {
            centre.disconnect();
            reveal.disconnect();
            band.clear();
        };
    }, [count]);

    return { setNode, active, revealed, enhanced };
};

// Mouse-like pointer only. Touch fires pointermove during a drag, and the whole
// effect is hover-shaped, so it is simply not wired up on those devices.
const hasFinePointer = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const ValueCard = ({ value, index, setNode, isActive, isRevealed }) => {
    const row = value.letterSide === "right" ? "md:flex-row-reverse" : "md:flex-row";
    const card = useRef(null);
    const frame = useRef(0);
    const [finePointer] = useState(hasFinePointer);

    // one stable callback ref feeding both the observers and the hover effect
    const setRefs = useCallback((el) => {
        card.current = el;
        setNode(index, el);
    }, [setNode, index]);

    // Pointer position is written straight to CSS custom properties on the node.
    // No state, so moving the mouse never re-renders React; the rAF gate keeps it
    // to one style write and one rect read per frame.
    const onPointerMove = useCallback((e) => {
        if (frame.current) return;
        const { clientX, clientY } = e;
        frame.current = requestAnimationFrame(() => {
            frame.current = 0;
            const el = card.current;
            if (!el) return;
            const r = el.getBoundingClientRect();
            if (!r.width || !r.height) return;
            const x = ((clientX - r.left) / r.width) * 100;
            const y = ((clientY - r.top) / r.height) * 100;
            el.style.setProperty("--pv-x", `${x.toFixed(2)}%`);
            el.style.setProperty("--pv-y", `${y.toFixed(2)}%`);
            // letter drifts toward the cursor, capped at 3px; gradient at 8px
            el.style.setProperty("--pv-dx", `${(((x - 50) / 50) * 3).toFixed(2)}px`);
            el.style.setProperty("--pv-dy", `${(((y - 50) / 50) * 3).toFixed(2)}px`);
            el.style.setProperty("--pv-gx", `${(((x - 50) / 50) * -8).toFixed(2)}px`);
            el.style.setProperty("--pv-gy", `${(((y - 50) / 50) * -8).toFixed(2)}px`);
        });
    }, []);

    const onPointerLeave = useCallback(() => {
        if (frame.current) {
            cancelAnimationFrame(frame.current);
            frame.current = 0;
        }
        const el = card.current;
        if (!el) return;
        // drop the offsets only; the CSS transition carries them back to rest
        el.style.setProperty("--pv-dx", "0px");
        el.style.setProperty("--pv-dy", "0px");
        el.style.setProperty("--pv-gx", "0px");
        el.style.setProperty("--pv-gy", "0px");
    }, []);

    useEffect(() => () => {
        if (frame.current) cancelAnimationFrame(frame.current);
    }, []);

    const pointerHandlers = finePointer ? { onPointerMove, onPointerLeave } : {};

    return (
        <li className={`flex ${value.align === "right" ? "justify-end" : "justify-start"}`}>
            <article
                ref={setRefs}
                data-index={index}
                data-active={isActive ? "true" : "false"}
                data-revealed={isRevealed ? "true" : "false"}
                className={`pv-card relative w-full ${value.width} lg:min-h-[360px] flex items-center overflow-hidden rounded-3xl`}
                style={{
                    border: value.border,
                    "--pv-shadow": value.shadow,
                    "--pv-shadow-hover": value.shadowHover,
                    "--pv-glow": value.glow,
                    "--pv-edge": value.edge,
                }}
                {...pointerHandlers}
            >
                {/* gradient rides its own layer, scaled a touch so drifting it
                    never exposes an edge */}
                <span aria-hidden="true" className="pv-grad" style={{ background: value.background }} />
                <span aria-hidden="true" className="pv-glow" />
                <span aria-hidden="true" className="pv-edge" />

                <div className={`relative z-[1] w-full flex flex-col items-center gap-8 md:gap-9 lg:gap-[44px] p-8 md:p-10 lg:p-[44px] ${row}`}>
                    {/* decorative: the title already names the value, so the
                        letter would only be noise in a screen reader */}
                    <span
                        aria-hidden="true"
                        className="pv-letter shrink-0 w-full md:w-[150px] lg:w-[200px] text-center font-bold leading-[0.78] text-[84px] md:text-[120px] lg:text-[168px]"
                        style={{ color: value.letterColor }}
                    >
                        {value.letter}
                    </span>

                    <div className="flex-1 min-w-0 flex flex-col gap-[18px]">
                        <h3
                            className="pv-reveal text-[24px] md:text-[28px] lg:text-[32px] font-semibold leading-[1.08]"
                            style={{ color: value.titleColor }}
                        >
                            {value.title}
                        </h3>
                        <p
                            className="pv-reveal pv-reveal-delayed text-base lg:text-[17px] leading-[1.62]"
                            style={{ color: value.bodyColor }}
                        >
                            {value.description}
                        </p>
                    </div>
                </div>
            </article>
        </li>
    );
};

const Beliefs = () => {
    const { setNode, active, revealed, enhanced } = useSpotlight(coreValues.length);

    return (
        <section
            id="values"
            aria-labelledby="values-heading"
            data-enhanced={enhanced ? "true" : "false"}
            className="w-full flex flex-col items-center px-6 container mx-auto py-24"
        >
            <style>{`
                /* Resting state is the visible one, so the section is complete
                   with no JS and whenever IntersectionObserver is unavailable.
                   Spotlight rules are gated on [data-enhanced="true"], set only
                   once the observers attach. Hover rules are gated on a fine
                   pointer, so touch devices get none of it. */
                .pv-card {
                    --pv-x: 50%;  --pv-y: 50%;
                    --pv-dx: 0px; --pv-dy: 0px;
                    --pv-gx: 0px; --pv-gy: 0px;
                    --pv-ls: 1;   --pv-lh: 1;
                    box-shadow: var(--pv-shadow);
                    transition: transform .75s cubic-bezier(.22,.61,.36,1), opacity .6s ease,
                                filter .6s ease, box-shadow .55s cubic-bezier(.22,.61,.36,1);
                    will-change: transform;
                }

                /* layers, painted under the copy (which carries z-[1]) */
                .pv-grad, .pv-glow, .pv-edge {
                    position: absolute; inset: 0; pointer-events: none; border-radius: inherit;
                }
                .pv-grad {
                    transform: scale(1.06) translate3d(var(--pv-gx), var(--pv-gy), 0);
                    transition: transform .7s cubic-bezier(.22,.61,.36,1);
                    will-change: transform;
                }
                .pv-glow {
                    opacity: 0;
                    transition: opacity .5s ease;
                    background: radial-gradient(42% 56% at var(--pv-x) var(--pv-y),
                        var(--pv-glow) 0%, transparent 72%);
                }
                .pv-edge {
                    opacity: 0;
                    border: 1px solid var(--pv-edge);
                    transition: opacity .5s ease;
                    -webkit-mask-image: radial-gradient(40% 52% at var(--pv-x) var(--pv-y), #000 0%, transparent 72%);
                            mask-image: radial-gradient(40% 52% at var(--pv-x) var(--pv-y), #000 0%, transparent 72%);
                }

                /* the letter's scale is the spotlight scale times the hover
                   scale, so the two effects compose instead of overwriting */
                .pv-letter {
                    transform: translate3d(var(--pv-dx), var(--pv-dy), 0) scale(calc(var(--pv-ls) * var(--pv-lh)));
                    transition: transform .6s cubic-bezier(.22,.61,.36,1);
                }
                .pv-reveal { transition: opacity .7s ease, transform .7s cubic-bezier(.22,.61,.36,1); }
                .pv-reveal-delayed { transition-delay: .09s; }

                @media (hover: hover) and (pointer: fine) {
                    .pv-card:hover { box-shadow: var(--pv-shadow-hover); --pv-lh: 1.015; }
                    .pv-card:hover .pv-glow, .pv-card:hover .pv-edge { opacity: 1; }
                    /* slower on the way out than on the way in */
                    .pv-card .pv-glow, .pv-card .pv-edge { transition-duration: .75s; }
                    .pv-card:hover .pv-glow, .pv-card:hover .pv-edge { transition-duration: .35s; }
                }

                [data-enhanced="true"] .pv-card { transform: scale(.98); opacity: .88; }
                [data-enhanced="true"] .pv-card[data-active="true"] {
                    transform: translateY(-10px) scale(1);
                    opacity: 1;
                    filter: brightness(1.03) contrast(1.02);
                }

                [data-enhanced="true"] .pv-card { --pv-ls: .97; }
                [data-enhanced="true"] .pv-card[data-active="true"] { --pv-ls: 1; }

                [data-enhanced="true"] .pv-reveal { opacity: 0; transform: translateY(12px); }
                [data-enhanced="true"] .pv-card[data-revealed="true"] .pv-reveal { opacity: 1; transform: none; }

                /* Mobile keeps the sequence but moves less. */
                @media (max-width: 767px) {
                    [data-enhanced="true"] .pv-card { transform: scale(.99); }
                    [data-enhanced="true"] .pv-card[data-active="true"] { transform: translateY(-4px) scale(1); }
                    [data-enhanced="true"] .pv-reveal { transform: translateY(8px); }
                }

                /* No movement at all. The border still brightens on hover, but
                   uniformly rather than tracking the cursor. */
                @media (prefers-reduced-motion: reduce) {
                    [data-enhanced="true"] .pv-card,
                    [data-enhanced="true"] .pv-card[data-active="true"],
                    [data-enhanced="true"] .pv-reveal,
                    [data-enhanced="true"] .pv-card[data-revealed="true"] .pv-reveal {
                        transform: none;
                        filter: none;
                    }
                    .pv-card, .pv-reveal { transition: opacity .3s ease; will-change: auto; }
                    .pv-card { box-shadow: var(--pv-shadow); }
                    .pv-card:hover { box-shadow: var(--pv-shadow); }
                    .pv-grad { transform: none; transition: none; will-change: auto; }
                    .pv-letter { transform: none; transition: none; }
                    .pv-glow { display: none; }
                    .pv-edge {
                        -webkit-mask-image: none;
                                mask-image: none;
                        transition: opacity .3s ease;
                    }
                    [data-enhanced="true"] .pv-card { opacity: .92; }
                    [data-enhanced="true"] .pv-card[data-active="true"] { opacity: 1; }
                    [data-enhanced="true"] .pv-reveal { opacity: 1; }
                }
            `}</style>

            {/* Figma 3777:19126 — the whole section sits in a 1280px column,
                header and cards separated by 96px */}
            <div className="w-full max-w-[1280px] flex flex-col gap-16 lg:gap-24">
                <div className="text-blue text-left w-full gap-6 flex flex-col">
                    <h2 id="values-heading" className="lg:text-5xl text-3xl font-bold text-blue20 pointer-events-none leading-tight lg:leading-[48px]">Our Core Values</h2>
                    <p className="lg:text-xl text-base leading-relaxed lg:leading-[1.75] text-grey45 pointer-events-none">We built PMG with the belief that a hands-on partner does more than execute. They show up with trust, clear communication, and a genuine investment in the way your business operates. Our Work Smarter Strategy drives results, but our values and commitment to meaningful work define who we are.</p>
                </div>

                <ul className="w-full flex flex-col gap-16 lg:gap-24 p-0 m-0">
                    {coreValues.map((value, index) => (
                        <ValueCard
                            key={value.id}
                            value={value}
                            index={index}
                            setNode={setNode}
                            isActive={active === index}
                            isRevealed={revealed.has(index)}
                        />
                    ))}
                </ul>
            </div>
        </section>
    );
};

export default Beliefs;
